import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { MailboxFilterOptions } from '../models/mailbox-filter-option';
import { MailboxRegisterOptions } from '../models/mailbox-register-options';
import { GetMailboxRegister, GetMailboxRegisterVariables } from '../queries/generated/GetMailboxRegister';
import { getExtensionMailboxQuery } from '../queries/getMailboxRegister.graphql';

@Injectable({ providedIn: 'root' })
export class MailboxRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getMailbox(
      endCursor: string | null,
      filters: MailboxFilterOptions[] | null,
      quickFilters: MailboxRegisterOptions | null,
      orderingOptions: OrderingOption[] | null
   ): Observable<ExecutionResult<GetMailboxRegister>> {
      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertMailboxFilterOption.bind(this)),
              }
            : { filterOptions: [] };
      if (quickFilters) {
         if (quickFilters.searchText) {
            const searchText = quickFilters.searchText.trim();
            const subExpression: FilterExpression = { filterOptions: [], logicalOperator: LogicalOperators.Or };
            subExpression.logicalOperator = LogicalOperators.Or;
            subExpression.filterOptions.push({
               field: 'Extension.Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            if (!isNaN(+quickFilters.searchText)) {
               subExpression.filterOptions.push({
                  field: 'Extension.Number',
                  operation: FilterOperations.Contains,
                  value: searchText,
               });
            }
            subExpression.filterOptions.push({
               field: 'Extension.Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'EmailAddress',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            filterBuilderFilterExpression.subExpressions = [subExpression];
         }
      }

      const variables = this.getMailboxVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetMailboxRegister, GetMailboxRegisterVariables>({
         query: getExtensionMailboxQuery,
         variables,
      });
   }

   private getMailboxVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetMailboxRegisterVariables {
      const variables: GetMailboxRegisterVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };
      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }

   private convertMailboxFilterOption(option: MailboxFilterOptions): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: MailboxFilterOptions): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'mailbox':
            return Array.isArray(option.value)
               ? option.value.map((v) => {
                    return v.mailboxId;
                 })
               : option.value?.mailboxId || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
   }
}
